import { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { AttachFileOutlined, CheckOutlined, FiberManualRecord } from "@mui/icons-material";

import * as yup from "yup";
import moment from "moment";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { theme } from "../../../theme/theme";
import { LoadingButton } from "@mui/lab";
import { toast } from "sonner";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

import {
  useDeleteRequestorAttachmentMutation,
  useEditRequestorConcernMutation,
  useLazyGetBackjobTicketsQuery,
  useLazyGetRequestorAttachmentQuery,
} from "../../../features/api_request/concerns/concernApi";

import { useDispatch } from "react-redux";
import { notificationApi } from "../../../features/api_notification/notificationApi";
import { notificationMessageApi } from "../../../features/api_notification_message/notificationMessageApi";
import {
  useLazyGetDownloadAttachmentQuery,
  useLazyGetViewAttachmentQuery,
} from "../../../features/api_attachments/attachmentsApi";
import {
  useLazyGetUsersQuery,
  useLazyGetUsersWithoutAdminQuery,
} from "../../../features/user_management_api/user/userApi";
import { useLazyGetCompanyQuery } from "../../../features/api masterlist/company/companyApi";
import { useLazyGetBusinessUnitQuery } from "../../../features/api masterlist/business-unit/businessUnitApi";
import { useLazyGetDepartmentQuery } from "../../../features/api masterlist/department/departmentApi";
import { useLazyGetUnitQuery } from "../../../features/api masterlist/unit/unitApi";
import { useLazyGetSubUnitQuery } from "../../../features/api masterlist/sub-unit/subUnitApi";
import { useLazyGetLocationWithPaginationQuery } from "../../../features/api masterlist/location/locationApi";
import { useLazyGetChannelsQuery } from "../../../features/api_channel_setup/channel/channelApi";
import { useLazyGetCategoryQuery } from "../../../features/api masterlist/category_api/categoryApi";
import { useLazyGetSubCategoryArrayQuery } from "../../../features/api masterlist/sub_category_api/subCategoryApi";
import ConcernViewMenuActions from "./ConcernViewMenuAction";
import { useLazyGetServiceProviderQuery } from "../../../features/api masterlist/service_provider/serviceProviderApi";
import { useLazyGetOneChargingQuery } from "../../../features/api masterlist/one_charging/oneChargingApi";
import { getAttachmentIcon } from "../../../utils/getAttachmentIcon";

const ConcernViewDialog = ({ editData, open, onClose }) => {
  const [attachments, setAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [ticketAttachmentId, setTicketAttachmentId] = useState(null);

  const [selectedImage, setSelectedImage] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  const [viewIsLoading, setViewIsLoading] = useState(false);
  const [downloadIsLoading, setDownloadIsLoading] = useState(false);
  const [deleteIsLoading, setDeleteIsLoading] = useState(false);

  const [backjobButton, setBackjobButton] = useState(false);

  const dateNeededValidation = new moment();
  const isScreenSmall = useMediaQuery(theme.breakpoints.down("md"));

  const requestorSchema = yup.object().shape({
    RequestConcernId: yup.string().nullable(),
    Concern: yup
      .string()
      .min(2, "Concern must be at least 2 characters long")
      .required("This field is required")
      .label("Ticket Details"),
    RequestAttachmentsFiles: yup.array().nullable(),

    Request_Type: yup.string().required("Request Type is required"),
    Severity: yup
      .string()
      .oneOf(["Normal", "Urgent"], "Invalid Severity")
      .required("Severity Type is required"),
    BackJobId: yup.object().notRequired(),
    Contact_Number: yup.string().matches(/^09\d{9}$/, {
      message: "Must start with 09 and be 11 digits long",
      excludeEmptyString: true,
    }),
    UserId: yup.object().required().label("Requestor is required"),
    CompanyId: yup.object().required().label("Company is required"),
    BusinessUnitId: yup.object().required().label("Business Unit"),
    DepartmentId: yup.object().required().label("Department is required"),
    UnitId: yup.object().required().label("Unit is required"),
    SubUnitId: yup.object().required().label("Sub Unit is required"),
    LocationId: yup.object().required().label("Location is required"),

    ServiceProvider: yup.object().required().label("Service Provider"),
    ChannelId: yup.object().notRequired(),
    CategoryId: yup.array(),
    SubCategoryId: yup.array(),
    DateNeeded: yup.date().required("Date needed is required"),
    TargetDate: yup.date().notRequired(),
    IssueHandler: yup.object().notRequired(),

    Notes: yup.string().notRequired(),
  });

  const fileInputRef = useRef();
  const dispatch = useDispatch();

  const [
    createEditRequestorConcern,
    { isLoading: isCreateEditRequestorConcernLoading, isFetching: isCreateEditRequestorConcernFetching },
  ] = useEditRequestorConcernMutation();

  const [getUser, { data: userData, isLoading: userIsLoading, isSuccess: userIsSuccess }] =
    useLazyGetUsersWithoutAdminQuery();
  const [
    getBackjobTicket,
    { data: backjobTicketData, isLoading: backjobTicketIsLoading, isSuccess: backjobTicketIsSuccess },
  ] = useLazyGetBackjobTicketsQuery();
  const [
    getChargingCode,
    { data: chargingCodeData, isLoading: chargingCodeIsLoading, isSuccess: chargingCodeIsSuccess },
  ] = useLazyGetOneChargingQuery();
  const [getCompany, { data: companyData, isLoading: companyIsLoading, isSuccess: companyIsSuccess }] =
    useLazyGetCompanyQuery();
  const [
    getBusinessUnit,
    { data: businessUnitData, isLoading: businessUnitIsLoading, isSuccess: businessUnitIsSuccess },
  ] = useLazyGetBusinessUnitQuery();
  const [
    getDepartment,
    { data: departmentData, isLoading: departmentIsLoading, isSuccess: departmentIsSuccess },
  ] = useLazyGetDepartmentQuery();
  const [getUnit, { data: unitData, isLoading: unitIsLoading, isSuccess: unitIsSuccess }] =
    useLazyGetUnitQuery();
  const [getSubUnit, { data: subUnitData, isLoading: subUnitIsLoading, isSuccess: subUnitIsSuccess }] =
    useLazyGetSubUnitQuery();
  const [getLocation, { data: locationData, isLoading: locationIsLoading, isSuccess: locationIsSuccess }] =
    useLazyGetLocationWithPaginationQuery();

  const [
    getServiceProvider,
    { data: serviceProviderData, isLoading: serviceProviderIsLoading, isSuccess: serviceProviderIsSuccess },
  ] = useLazyGetServiceProviderQuery();
  const [
    getIssueHandler,
    { data: issueHandlerData, isLoading: issueHandlerIsLoading, isSuccess: issueHandlerIsSuccess },
  ] = useLazyGetChannelsQuery();
  const [getCategory, { data: categoryData, isLoading: categoryIsLoading, isSuccess: categoryIsSuccess }] =
    useLazyGetCategoryQuery();
  const [
    getSubCategory,
    { data: subCategoryData, isLoading: subCategoryIsLoading, isSuccess: subCategoryIsSuccess },
  ] = useLazyGetSubCategoryArrayQuery();

  const [getRequestorAttachment] = useLazyGetRequestorAttachmentQuery();
  const [getViewAttachment] = useLazyGetViewAttachmentQuery();
  const [getDownloadAttachment] = useLazyGetDownloadAttachmentQuery();
  const [deleteRequestorAttachment] = useDeleteRequestorAttachmentMutation();

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(requestorSchema),
    mode: "onChange",
    defaultValues: {
      Concern: "",
      RequestConcernId: "",
      RequestAttachmentsFiles: [],

      Request_Type: "",
      Severity: "Normal",
      BackJobId: null,
      Contact_Number: "",

      UserId: null,
      ChargingCode: null,
      CompanyId: null,
      BusinessUnitId: null,
      DepartmentId: null,
      UnitId: null,
      SubUnitId: null,
      LocationId: null,

      ServiceProvider: null,
      ChannelId: null,
      CategoryId: [],
      SubCategoryId: [],
      DateNeeded: null,
      TargetDate: null,
      IssueHandler: null,

      Notes: "",
    },
  });

  const onConcernFormSubmit = (formData) => {
    const payload = new FormData();

    payload.append("RequestConcernId", formData.RequestConcernId);
    payload.append("TicketConcernId", editData?.ticketRequestConcerns[0]?.ticketConcernId);

    payload.append("Request_Type", formData.Request_Type);
    payload.append("Severity", formData.Severity);
    payload.append("BackJobId", formData?.BackJobId === null ? "" : formData.BackJobId?.ticketConcernId);
    payload.append("UserId", formData.UserId?.id);
    payload.append("Contact_Number", formData.Contact_Number);
    payload.append("CompanyId", formData.CompanyId?.id);
    payload.append("BusinessUnitId", formData.BusinessUnitId?.id);
    payload.append("DepartmentId", formData.DepartmentId?.id);
    payload.append("UnitId", formData.UnitId?.id);
    payload.append("SubUnitId", formData.SubUnitId?.id);
    payload.append("Location_Code", formData.LocationId?.location_Code);

    payload.append("ServiceProviderId", formData.ServiceProvider?.id);
    payload.append("ChannelId", formData.ChannelId ? formData.ChannelId?.channelId : 0);
    payload.append("DateNeeded", moment(formData.DateNeeded).format("YYYY-MM-DD"));
    payload.append("TargetDate", formData.TargetDate ? moment(formData.TargetDate).format("YYYY-MM-DD") : "");
    payload.append("AssignTo", formData.IssueHandler ? formData.IssueHandler?.userId : "");
    payload.append("Concern", formData.Concern);
    payload.append("Notes", formData.Notes);

    const category = formData.CategoryId;
    for (let i = 0; i < category.length; i++) {
      payload.append(`AddRequestTicketCategory[${i}].ticketCategoryId`, category[i].ticketCategoryId || "");
      payload.append(`AddRequestTicketCategory[${i}].categoryId`, category[i]?.id);
    }

    if (category.length === 0) {
      payload.append(`AddRequestTicketCategory[0].ticketCategoryId`, "");
      payload.append(`AddRequestTicketCategory[0].categoryId`, "");
    }

    const subCategory = formData.SubCategoryId;
    for (let i = 0; i < subCategory.length; i++) {
      payload.append(
        `AddRequestTicketSubCategory[${i}].ticketSubCategoryId`,
        subCategory[i].ticketSubCategoryId || "",
      );
      payload.append(`AddRequestTicketSubCategory[${i}].subCategoryId`, subCategory[i]?.subCategoryId);
    }

    if (subCategory.length === 0) {
      payload.append(`AddRequestTicketSubCategory[0].ticketSubCategoryId`, "");
      payload.append(`AddRequestTicketSubCategory[0].subCategoryId`, "");
    }

    const files = formData.RequestAttachmentsFiles;
    for (let i = 0; i < files.length; i++) {
      payload.append(`RequestAttachmentsFile[${i}].ticketAttachmentId`, files[i].ticketAttachmentId || "");
      payload.append(`RequestAttachmentsFile[${i}].attachment`, files[i]);
    }

    if (files.length === 0) {
      payload.append(`RequestAttachmentsFile[0].ticketAttachmentId`, "");
      payload.append(`RequestAttachmentsFile[0].attachment`, "");
    }

    // console.log("Payload Entries: ", [...payload.entries()]);

    createEditRequestorConcern(payload)
      .unwrap()
      .then(() => {
        toast.success("Success!", {
          description: "Concern updated successfully!",
          duration: 1500,
        });
        dispatch(notificationApi.util.resetApiState());
        dispatch(notificationMessageApi.util.resetApiState());
        setAttachments([]);
        reset({
          Request_Type: "New Request",
          Severity: "Normal",
          BackJobId: null,
          Contact_Number: "",

          DateNeeded: moment().format("YYYY-MM-DD"),
          ChannelId: null,
          CategoryId: [],
          SubCategoryId: [],

          Concern: "",
          Notes: "",
        });
        setIsLoading(false);
        onClose();
      })
      .catch((err) => {
        console.log("Error", err);
        toast.error("Error!", {
          description: err.data.error.message,
          duration: 1500,
        });
      });
  };

  const handleAttachments = (event) => {
    const newFiles = Array.from(event.target.files);

    const fileNames = newFiles.map((file) => ({
      name: file.name,
      size: (file.size / (1024 * 1024)).toFixed(2),
      file,
    }));

    const uniqueNewFiles = fileNames.filter(
      (newFile) => !attachments?.some((existingFile) => existingFile.name === newFile.name),
    );
    setAttachments((prevFiles) =>
      Array.isArray(prevFiles) ? [...prevFiles, ...uniqueNewFiles] : [...uniqueNewFiles],
    );
  };

  const handleUploadButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleDeleteFile = async (fileNameToDelete) => {
    setDeleteIsLoading(true);
    try {
      if (fileNameToDelete.ticketAttachmentId) {
        const deletePayload = {
          ticketAttachmentId: fileNameToDelete.ticketAttachmentId,
        };
        await deleteRequestorAttachment(deletePayload).unwrap();
      }

      setAttachments((prevFiles) => prevFiles.filter((fileName) => fileName !== fileNameToDelete));

      setValue(
        "RequestAttachmentsFiles",
        watch("RequestAttachmentsFiles").filter((file) => file.name !== fileNameToDelete.name),
      );
      setDeleteIsLoading(false);
    } catch (error) {}
  };

  // Multiple files handler event
  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const onCloseAction = () => {
    reset({
      Request_Type: "New Request",
      Severity: "Normal",
      BackJobId: null,
      Contact_Number: "",

      DateNeeded: null,
      ChannelId: null,
      CategoryId: [],
      SubCategoryId: [],

      Concern: "",
      Notes: "",
    });
    setAttachments([]);
    onClose();
  };

  const getAttachmentData = async (id) => {
    try {
      const res = await getRequestorAttachment({ Id: id }).unwrap();

      setAttachments(
        res?.value?.[0]?.attachments?.map((item) => ({
          ticketAttachmentId: item.ticketAttachmentId,
          name: item.fileName,
          size: (item.fileSize / (1024 * 1024)).toFixed(2),
          link: item.attachment,
        })),
      );
    } catch (error) {}
  };

  // Function to open image view dialog
  const handleViewImage = async (file) => {
    setViewIsLoading(true);
    try {
      const response = await getViewAttachment(file?.ticketAttachmentId);

      if (response?.data) {
        const imageUrl = URL.createObjectURL(response.data); // Create a URL for the fetched image
        setSelectedImage(imageUrl); // Set the image URL to state
        setIsViewDialogOpen(true);
      }
      setViewIsLoading(false);
    } catch (error) {
      console.error("Error fetching image:", error);
      toast.error("Error!", {
        description: "View file error!",
        duration: 1500,
      });
      setViewIsLoading(false);
    }
  };

  const handleViewImageWithoutId = (file) => {
    setViewIsLoading(true);
    try {
      const reader = new FileReader();

      reader.onload = (e) => {
        setSelectedImage(e.target.result);
        setIsViewDialogOpen(true);
      };
      reader.readAsDataURL(file);
      setViewIsLoading(false);
    } catch (error) {
      console.error("Error fetching image:", error);

      toast.error("Error!", {
        description: "View file error!",
        duration: 1500,
      });
      setViewIsLoading(false);
    }
  };

  const handleDownloadAttachment = async (file) => {
    setDownloadIsLoading(true);
    try {
      const response = await getDownloadAttachment(file?.ticketAttachmentId);

      if (response?.data) {
        const blob = new Blob([response.data], { type: response.data.type });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${file?.name || "attachment"}`); // Default to 'attachment' if no name
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link); // Clean up after download
        setDownloadIsLoading(false);
      }
    } catch (error) {
      console.log("Error", error);
      toast.error("Error!", {
        description: "Download file error!",
        duration: 1500,
      });
      setDownloadIsLoading(false);
    }
  };

  const handleViewClose = () => {
    setIsViewDialogOpen(false);
    setSelectedImage(null);
  };

  const isImageFile = (fileName) => {
    return /\.(jpg|jpeg|png)$/i.test(fileName);
  };

  useEffect(() => {
    if (editData) {
      if (!chargingCodeIsSuccess) getChargingCode();
      if (!companyIsSuccess) getCompany();
      if (!businessUnitIsSuccess) getBusinessUnit();
      if (!departmentIsSuccess) getDepartment();
      if (!unitIsSuccess) getUnit();
      if (!subUnitIsSuccess) getSubUnit();
      if (!locationIsSuccess) getLocation();
      if (!serviceProviderIsSuccess) getServiceProvider({ Status: true });

      setValue("RequestConcernId", editData?.requestConcernId);
      setValue("Request_Type", editData?.request_Type);
      setValue("Severity", editData?.severity);
      setValue("Concern", editData?.concern);

      if (editData?.request_Type === "New Request") {
        setValue("BackJobId", null);
        setBackjobButton(false);
      } else {
        setValue("BackJobId", {
          ticketConcernId: editData?.backJobId,
          concern: editData?.back_Job_Concern,
        });
        setBackjobButton(true);
      }

      setValue("UserId", {
        id: editData?.requestorId,
        fullname: editData?.fullName,
      });

      if (editData.contact_Number === null) {
        setValue("Contact_Number", "");
      } else {
        setValue("Contact_Number", editData.contact_Number);
      }

      // setValue("ChargingCode", {
      //   code: userInformation?.oneChargingCode,
      //   name: userInformation?.oneChargingName,
      // });

      setValue("CompanyId", {
        id: editData?.companyId,
        company_Code: editData?.company_Code,
        company_Name: editData?.company_Name,
      });

      setValue("BusinessUnitId", {
        id: editData?.businessUnitId,
        business_Code: editData?.businessUnit_Code,
        business_Name: editData?.businessUnit_Name,
      });

      setValue("DepartmentId", {
        id: editData?.departmentId,
        department_Code: editData?.department_Code,
        department_Name: editData?.department_Name,
      });

      setValue("UnitId", {
        id: editData?.unitId,
        unit_Code: editData?.unit_Code,
        unit_Name: editData?.unit_Name,
      });

      setValue("SubUnitId", {
        id: editData?.subUnitId,
        subUnit_Code: editData?.subUnit_Code,
        subUnit_Name: editData?.subUnit_Name,
      });

      setValue("LocationId", {
        location_Code: editData?.location_Code,
        location_Name: editData?.location_Name,
      });

      if (editData?.channelId !== null) {
        setValue("ServiceProvider", {
          id: editData?.serviceProviderId,
          serviceProviderName: editData?.serviceProviderName,
        });
      } else {
        getServiceProvider({
          Status: true,
        });
        setValue("ServiceProvider", {
          id: editData?.serviceProviderId,
          serviceProviderName: editData?.serviceProviderName,
        });
      }

      if (editData?.channelId !== null) {
        setValue("ChannelId", {
          channelId: editData?.channelId,
          channelName: editData?.channel_Name,
        });
      } else {
        setValue("ChannelId", null);
      }

      setValue("DateNeeded", moment(editData?.date_Needed));

      if (editData?.targetDate !== null) {
        setValue("TargetDate", moment(editData?.targetDate));
      } else {
        setValue("TargetDate", null);
      }

      if (editData?.assignTo !== null) {
        setValue("IssueHandler", {
          userId: editData?.assignTo,
          fullname: editData?.assignToName,
        });
      } else {
        setValue("IssueHandler", null);
      }

      const category = editData?.getRequestTicketCategories?.map((item) => ({
        ticketCategoryId: item.ticketCategoryId,
        id: item.categoryId,
        category_Description: item.category_Description,
      }));

      const subCategory = editData?.getRequestSubTicketCategories.map((item) => ({
        ticketSubCategoryId: item.ticketSubCategoryId,
        subCategoryId: item.subCategoryId,
        sub_Category_Description: item.subCategory_Description,
      }));

      const categoryIdParams = editData?.getRequestTicketCategories?.map((item) => item?.categoryId);

      setValue("CategoryId", category);
      setValue("SubCategoryId", subCategory);

      if (editData.notes === null) {
        setValue("Notes", "");
      } else {
        setValue("Notes", editData.notes);
      }

      getAttachmentData(editData.ticketRequestConcerns.map((item) => item.ticketConcernId));
      getSubCategory({
        CategoryId: categoryIdParams,
      });
    }
  }, [
    editData,
    companyIsLoading,
    businessUnitIsLoading,
    departmentIsLoading,
    unitIsLoading,
    subUnitIsLoading,
    locationIsLoading,
  ]);

  // useEffect(() => {
  //   const selectedCategories = watch("CategoryId");
  //   const selectedSubCategories = watch("SubCategoryId");

  //   if (selectedCategories.length > 0) {
  //     const filteredSubCategories = selectedSubCategories.filter((subCategory) =>
  //       selectedCategories.some((category) => subCategoryData?.value?.some((item) => item.subCategoryId === subCategory.subCategoryId && item.categoryId === category.id)),
  //     );
  //     setValue("SubCategoryId", filteredSubCategories);
  //   } else {
  //     setValue("SubCategoryId", []);
  //   }
  // }, [subCategoryData]);

  // useEffect(() => {

  // }, [editData?.backJobId]);

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="lg"
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick") {
            event.stopPropagation();
            return;
          }
          onCloseAction();
        }}
        disableEscapeKeyDown={false}
        sx={{ borderRadius: "none", padding: 0, backdropFilter: "blur(5px)" }}
        PaperProps={{ style: { overflow: "auto" } }}
      >
        <form onSubmit={handleSubmit(onConcernFormSubmit)}>
          <DialogContent sx={{ paddingBottom: 8 }}>
            <Stack direction="column" sx={{ padding: "1px", minHeight: "700px" }}>
              <Stack>
                <Stack direction="row" gap={0.5}>
                  <Typography
                    sx={{
                      fontSize: "18px",
                      fontWeight: 700,
                      color: "#48BB78",
                    }}
                  >
                    {!editData ? "New Incident" : "View Incident"}
                  </Typography>
                </Stack>
              </Stack>

              <Stack padding={3}>
                <Typography
                  sx={{
                    fontSize: "15px",
                    fontWeight: 500,
                  }}
                >
                  {`Ticket Number: ${moment(editData?.created_At).format("YYYY")}-${editData?.ticketRequestConcerns?.[0]?.ticketConcernId}`}
                </Typography>

                {/* REQUEST TYPE */}
                <Stack
                  width="100%"
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 1,
                    mt: 2,
                  }}
                >
                  <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
                    <Typography sx={{ fontSize: "13px" }}>Request Type</Typography>
                    <FiberManualRecord color="error" sx={{ fontSize: "10px" }} />
                  </Stack>

                  <Controller
                    name="Request_Type"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Select Request type"
                        size="small"
                        sx={{ width: "100%", fontSize: "13px" }}
                        disabled={backjobButton || editData?.concern_Status !== "For Approval"}
                      >
                        <MenuItem value="New Request" sx={{ fontSize: "13px" }}>
                          New Request
                        </MenuItem>
                        <MenuItem value="Rework" sx={{ fontSize: "13px" }}>
                          Rework
                        </MenuItem>
                      </Select>
                    )}
                    // disabled={}
                  />
                </Stack>

                {watch("Request_Type") === "Rework" && (
                  <Stack sx={{ width: "100%", mb: 1 }}>
                    <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
                      <Typography sx={{ fontSize: "13px" }}>Ticket Number</Typography>
                      <FiberManualRecord color="error" sx={{ fontSize: "10px" }} />
                    </Stack>

                    <Controller
                      control={control}
                      name="BackJobId"
                      render={({ field: { ref, value, onChange } }) => {
                        return (
                          <Autocomplete
                            ref={ref}
                            size="small"
                            value={value}
                            options={backjobTicketData?.value || []}
                            loading={backjobTicketIsLoading}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select Ticket Number"
                                sx={{ "& .MuiInputBase-input": { fontSize: "13px" } }}
                              />
                            )}
                            onOpen={() => {
                              if (!backjobTicketIsSuccess) getBackjobTicket();
                            }}
                            onChange={(_, value) => onChange(value)}
                            getOptionLabel={(option) => `${option?.ticketConcernId} - ${option.concern}`}
                            noOptionsText={"No tickets available for backjob request"}
                            isOptionEqualToValue={(option, value) =>
                              option.ticketConcernId === value.ticketConcernId
                            }
                            fullWidth
                            disablePortal
                            disableClearable
                            disabled={editData?.concern_Status !== "For Approval" || backjobButton}
                            componentsProps={{
                              popper: {
                                sx: {
                                  "& .MuiAutocomplete-listbox": {
                                    fontSize: "13px",
                                  },
                                },
                              },
                            }}
                          />
                        );
                      }}
                    />
                  </Stack>
                )}

                {/* SEVERITY*/}
                <Stack
                  width="100%"
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 1,
                  }}
                >
                  <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
                    <Typography sx={{ fontSize: "13px" }}>Severity</Typography>
                    <FiberManualRecord color="error" sx={{ fontSize: "10px" }} />
                  </Stack>

                  <Controller
                    name="Severity"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        displayEmpty
                        size="small"
                        placeholder="Select Severity"
                        inputProps={{ "aria-label": "Without label" }}
                        sx={{
                          width: "100%",
                          fontSize: "13px",
                          "& .MuiSelect-select": {
                            color: "#fff",
                          },
                        }}
                        disabled={editData?.concern_Status !== "For Approval" || backjobButton}
                      >
                        <MenuItem value="Normal" sx={{ fontSize: "13px" }}>
                          Normal
                        </MenuItem>

                        <MenuItem value="Urgent" sx={{ fontSize: "13px" }}>
                          Urgent
                        </MenuItem>
                      </Select>
                    )}
                  />
                </Stack>

                {/* REQUESTOR DETAILS */}
                <Typography sx={{ fontSize: "15px", color: theme.palette.primary.main, mb: 1 }}>
                  Requestor Details
                </Typography>
                <Stack direction="row" sx={{ width: "100%" }}>
                  <Stack direction={isScreenSmall ? "column" : "row"} sx={{ width: "100%", gap: 1 }}>
                    <Stack sx={{ width: isScreenSmall ? "100%" : "50%" }}>
                      <Typography sx={{ fontSize: "13px" }}>Requestor Name</Typography>
                      <Controller
                        control={control}
                        name="UserId"
                        render={({ field: { ref, value, onChange } }) => {
                          return (
                            <Autocomplete
                              ref={ref}
                              size="small"
                              value={value}
                              options={userData?.value?.users || []}
                              loading={userIsLoading}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Requestor"
                                  sx={{ "& .MuiInputBase-input": { fontSize: "13px" } }}
                                />
                              )}
                              onOpen={() => {
                                if (!userIsSuccess)
                                  getUser({
                                    Status: true,
                                  });
                              }}
                              onChange={(_, value) => {
                                setValue("CompanyId", {
                                  id: value?.companyId,
                                  company_Code: value?.company_Code,
                                  company_Name: value?.company_Name,
                                });
                                setValue("BusinessUnitId", {
                                  id: value?.businessUnitId,
                                  business_Code: value?.businessUnit_Code,
                                  business_Name: value?.businessUnit_Name,
                                });
                                setValue("DepartmentId", {
                                  id: value?.departmentId,
                                  department_Code: value?.department_Code,
                                  department_Name: value?.department_Name,
                                });
                                setValue("UnitId", {
                                  id: value?.unitId,
                                  unit_Code: value?.unit_Code,
                                  unit_Name: value?.unit_Name,
                                });
                                setValue("SubUnitId", {
                                  id: value?.subUnitId,
                                  subUnit_Code: value?.subUnit_Code,
                                  subUnit_Name: value?.subUnit_Name,
                                });
                                setValue("LocationId", {
                                  location_Code: value?.location_Code,
                                  location_Name: value?.location_Name,
                                });

                                onChange(value);
                              }}
                              getOptionLabel={(option) => option?.fullname}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              fullWidth
                              disabled
                              disablePortal
                              disableClearable
                              componentsProps={{
                                popper: {
                                  sx: {
                                    "& .MuiAutocomplete-listbox": {
                                      fontSize: "13px",
                                    },
                                  },
                                },
                              }}
                            />
                          );
                        }}
                      />
                    </Stack>

                    <Stack sx={{ width: isScreenSmall ? "100%" : "50%" }}>
                      <Typography sx={{ fontSize: "13px" }}>Contact Number (Optional)</Typography>
                      <Controller
                        control={control}
                        name="Contact_Number"
                        rules={{
                          required: "Contact number is required",
                          pattern: {
                            value: /^[0-9]{11}$/,
                            message: "Contact number must be exactly 11 digits",
                          },
                        }}
                        render={({ field: { ref, value, onChange } }) => {
                          const handleInputChange = (event) => {
                            let input = event.target.value;

                            if (input && !input.startsWith("09")) {
                              input = "09" + input.replace(/[^0-9]/g, "");
                            } else {
                              input = input.replace(/[^0-9]/g, "");
                            }

                            if (input.length > 11) {
                              input = input.slice(0, 11);
                            }

                            onChange(input || null);
                          };

                          return (
                            <TextField
                              inputRef={ref}
                              size="small"
                              value={value}
                              placeholder="09"
                              onChange={handleInputChange}
                              error={!!errors.Contact_Number}
                              helperText={errors.Contact_Number ? errors.Contact_Number.message : ""}
                              InputProps={{
                                style: {
                                  fontSize: "13px",
                                },
                              }}
                              InputLabelProps={{
                                style: {
                                  fontSize: "13px",
                                },
                              }}
                              autoComplete="off"
                              sx={{
                                width: "100%",
                              }}
                              disabled={editData?.concern_Status !== "For Approval" || backjobButton}
                              fullWidth
                            />
                          );
                        }}
                      />
                    </Stack>
                  </Stack>
                </Stack>

                <Stack direction="row" sx={{ width: "100%", mt: 1 }}>
                  <Stack direction={isScreenSmall ? "column" : "row"} sx={{ width: "100%", gap: 1 }}>
                    <Stack sx={{ width: isScreenSmall ? "100%" : "50%" }}>
                      <Typography sx={{ fontSize: "13px" }}>Company</Typography>
                      <Controller
                        control={control}
                        name="CompanyId"
                        render={({ field: { ref, value, onChange } }) => {
                          return (
                            <Autocomplete
                              ref={ref}
                              size="small"
                              value={value}
                              options={companyData?.value?.company || []}
                              loading={companyIsLoading}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Company"
                                  sx={{ "& .MuiInputBase-input": { fontSize: "13px" } }}
                                />
                              )}
                              onOpen={() => {
                                if (!companyIsSuccess) getCompany();
                              }}
                              onChange={(_, value) => onChange(value)}
                              getOptionLabel={(option) => `${option.company_Code} - ${option.company_Name}`}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              sx={{
                                flex: 2,
                              }}
                              fullWidth
                              disabled
                              disablePortal
                              disableClearable
                              componentsProps={{
                                popper: {
                                  sx: {
                                    "& .MuiAutocomplete-listbox": {
                                      fontSize: "13px",
                                    },
                                  },
                                },
                              }}
                            />
                          );
                        }}
                      />
                    </Stack>

                    <Stack sx={{ width: isScreenSmall ? "100%" : "50%" }}>
                      <Typography sx={{ fontSize: "13px" }}>Business Unit</Typography>
                      <Controller
                        control={control}
                        name="BusinessUnitId"
                        render={({ field: { ref, value, onChange } }) => {
                          return (
                            <Autocomplete
                              ref={ref}
                              size="small"
                              value={value}
                              options={
                                businessUnitData?.value?.businessUnit?.filter(
                                  (item) => item?.companyId === watch("CompanyId")?.id,
                                ) || []
                              }
                              loading={businessUnitIsLoading}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Business Unit"
                                  sx={{ "& .MuiInputBase-input": { fontSize: "13px" } }}
                                />
                              )}
                              onChange={(_, value) => onChange(value)}
                              getOptionLabel={(option) => `${option.business_Code} - ${option.business_Name}`}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              sx={{
                                flex: 2,
                              }}
                              fullWidth
                              disabled
                              disablePortal
                              disableClearable
                              componentsProps={{
                                popper: {
                                  sx: {
                                    "& .MuiAutocomplete-listbox": {
                                      fontSize: "13px",
                                    },
                                  },
                                },
                              }}
                            />
                          );
                        }}
                      />
                    </Stack>
                  </Stack>
                </Stack>

                <Stack direction="row" sx={{ width: "100%", mt: 1 }}>
                  <Stack direction={isScreenSmall ? "column" : "row"} sx={{ width: "100%", gap: 1 }}>
                    <Stack sx={{ width: isScreenSmall ? "100%" : "50%" }}>
                      <Typography sx={{ fontSize: "13px" }}>Department</Typography>
                      <Controller
                        control={control}
                        name="DepartmentId"
                        render={({ field: { ref, value, onChange } }) => {
                          return (
                            <Autocomplete
                              ref={ref}
                              size="small"
                              value={value}
                              options={
                                departmentData?.value?.department?.filter(
                                  (item) => item.businessUnitId === watch("BusinessUnitId")?.id,
                                ) || []
                              }
                              loading={departmentIsLoading}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Department"
                                  sx={{ "& .MuiInputBase-input": { fontSize: "13px" } }}
                                />
                              )}
                              onChange={(_, value) => onChange(value)}
                              getOptionLabel={(option) =>
                                `${option.department_Code} - ${option.department_Name}`
                              }
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              sx={{
                                flex: 2,
                              }}
                              fullWidth
                              disabled
                              disablePortal
                              disableClearable
                              componentsProps={{
                                popper: {
                                  sx: {
                                    "& .MuiAutocomplete-listbox": {
                                      fontSize: "13px",
                                    },
                                  },
                                },
                              }}
                            />
                          );
                        }}
                      />
                    </Stack>

                    <Stack sx={{ width: isScreenSmall ? "100%" : "50%" }}>
                      <Typography sx={{ fontSize: "13px" }}>Unit</Typography>
                      <Controller
                        control={control}
                        name="UnitId"
                        render={({ field: { ref, value, onChange } }) => {
                          return (
                            <Autocomplete
                              ref={ref}
                              size="small"
                              value={value}
                              options={
                                unitData?.value?.unit.filter(
                                  (item) => item.departmentId === watch("DepartmentId")?.id,
                                ) || []
                              }
                              loading={unitIsLoading}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Unit"
                                  sx={{ "& .MuiInputBase-input": { fontSize: "13px" } }}
                                />
                              )}
                              onChange={(_, value) => onChange(value)}
                              getOptionLabel={(option) => `${option.unit_Code} - ${option.unit_Name}`}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              sx={{
                                flex: 2,
                              }}
                              fullWidth
                              disabled
                              disablePortal
                              disableClearable
                              componentsProps={{
                                popper: {
                                  sx: {
                                    "& .MuiAutocomplete-listbox": {
                                      fontSize: "13px",
                                    },
                                  },
                                },
                              }}
                            />
                          );
                        }}
                      />
                    </Stack>
                  </Stack>
                </Stack>

                <Stack direction="row" sx={{ width: "100%", mt: 1 }}>
                  <Stack direction={isScreenSmall ? "column" : "row"} sx={{ width: "100%", gap: 1 }}>
                    <Stack sx={{ width: isScreenSmall ? "100%" : "50%" }}>
                      <Typography sx={{ fontSize: "13px" }}>Sub Unit</Typography>
                      <Controller
                        control={control}
                        name="SubUnitId"
                        render={({ field: { ref, value, onChange } }) => {
                          return (
                            <Autocomplete
                              ref={ref}
                              size="small"
                              value={value}
                              options={
                                subUnitData?.value?.subUnit?.filter(
                                  (item) => item.unitId === watch("UnitId")?.id,
                                ) || []
                              }
                              loading={subUnitIsLoading}
                              onOpen={() => {
                                if (!subUnitIsSuccess)
                                  getSubUnit({
                                    Status: true,
                                  });
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Sub Unit"
                                  sx={{ "& .MuiInputBase-input": { fontSize: "13px" } }}
                                />
                              )}
                              onChange={(_, value) => onChange(value)}
                              getOptionLabel={(option) => `${option.subUnit_Code} - ${option.subUnit_Name}`}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              sx={{
                                flex: 2,
                              }}
                              fullWidth
                              disabled
                              disablePortal
                              disableClearable
                              componentsProps={{
                                popper: {
                                  sx: {
                                    "& .MuiAutocomplete-listbox": {
                                      fontSize: "13px",
                                    },
                                  },
                                },
                              }}
                            />
                          );
                        }}
                      />
                    </Stack>

                    <Stack sx={{ width: isScreenSmall ? "100%" : "50%" }}>
                      <Typography sx={{ fontSize: "13px" }}>Location</Typography>
                      <Controller
                        control={control}
                        name="LocationId"
                        render={({ field: { ref, value, onChange } }) => {
                          return (
                            <Autocomplete
                              ref={ref}
                              size="small"
                              value={value}
                              options={
                                locationData?.value?.location.filter((item) =>
                                  item.subUnits.some(
                                    (subUnitItem) => subUnitItem.subUnitId === watch("SubUnitId")?.id,
                                  ),
                                ) || []
                              }
                              loading={locationIsLoading}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Location"
                                  sx={{ "& .MuiInputBase-input": { fontSize: "13px" } }}
                                />
                              )}
                              onChange={(_, value) => onChange(value)}
                              getOptionLabel={(option) => `${option.location_Code} - ${option.location_Name}`}
                              isOptionEqualToValue={(option, value) =>
                                option.location_Code === value.location_Code
                              }
                              sx={{
                                flex: 2,
                              }}
                              fullWidth
                              disabled
                              disablePortal
                              disableClearable
                              componentsProps={{
                                popper: {
                                  sx: {
                                    "& .MuiAutocomplete-listbox": {
                                      fontSize: "13px",
                                    },
                                  },
                                },
                              }}
                            />
                          );
                        }}
                      />
                    </Stack>
                  </Stack>
                </Stack>

                {/* CONCERN INFORMATION */}
                <Typography sx={{ fontSize: "15px", color: theme.palette.primary.main, mt: 2 }}>
                  Concern Details
                </Typography>

                <Stack direction="row" sx={{ width: "100%", mt: 1 }}>
                  <Stack direction={isScreenSmall ? "column" : "row"} sx={{ width: "100%", gap: 1 }}>
                    <Stack sx={{ width: isScreenSmall ? "100%" : "50%" }}>
                      {/* Service Provider */}
                      <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
                        <Typography sx={{ fontSize: "13px" }}>Service Provider</Typography>
                        <FiberManualRecord color="error" sx={{ fontSize: "10px" }} />
                      </Stack>

                      <Controller
                        control={control}
                        name="ServiceProvider"
                        render={({ field: { ref, value, onChange } }) => {
                          return (
                            <Autocomplete
                              ref={ref}
                              size="small"
                              value={value}
                              options={serviceProviderData?.value?.serviceProvider || []}
                              loading={serviceProviderIsLoading}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Select Service Provider"
                                  sx={{ "& .MuiInputBase-input": { fontSize: "13px" } }}
                                />
                              )}
                              onOpen={() => {
                                if (!serviceProviderIsSuccess)
                                  getServiceProvider({
                                    Status: true,
                                  });
                              }}
                              onChange={(_, value) => {
                                onChange(value);

                                setValue("ChannelId", null);
                                setValue("CategoryId", []);
                                setValue("SubCategoryId", []);
                              }}
                              getOptionLabel={(option) => option.serviceProviderName}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              sx={{
                                flex: 2,
                              }}
                              fullWidth
                              disablePortal
                              disableClearable
                              // disabled={editData?.concern_Status !== "For Approval" || backjobButton}
                              disabled
                              componentsProps={{
                                popper: {
                                  sx: {
                                    "& .MuiAutocomplete-listbox": {
                                      fontSize: "12px",
                                    },
                                  },
                                },
                              }}
                            />
                          );
                        }}
                      />
                    </Stack>

                    <Stack sx={{ width: isScreenSmall ? "100%" : "50%" }}>
                      {/* Channel */}
                      <Typography sx={{ fontSize: "13px" }}>Channel</Typography>
                      <Controller
                        control={control}
                        name="ChannelId"
                        render={({ field: { ref, value, onChange } }) => {
                          return (
                            <Autocomplete
                              ref={ref}
                              size="small"
                              value={value}
                              options={
                                editData
                                  ? serviceProviderData?.value?.serviceProvider?.find(
                                      (item) => item.id === watch("ServiceProvider")?.id,
                                    )?.serviceChannel || []
                                  : watch("ServiceProvider")?.serviceChannel || []
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Select Channel"
                                  sx={{ "& .MuiInputBase-input": { fontSize: "13px" } }}
                                />
                              )}
                              onChange={(_, value) => {
                                onChange(value);

                                setValue("CategoryId", []);
                                setValue("SubCategoryId", []);
                              }}
                              getOptionLabel={(option) => option.channelName}
                              isOptionEqualToValue={(option, value) => option.channelId === value.channelId}
                              sx={{
                                flex: 2,
                              }}
                              fullWidth
                              disabled
                              disablePortal
                              disableClearable
                              componentsProps={{
                                popper: {
                                  sx: {
                                    "& .MuiAutocomplete-listbox": {
                                      fontSize: "13px",
                                    },
                                  },
                                },
                              }}
                            />
                          );
                        }}
                      />
                    </Stack>
                  </Stack>
                </Stack>

                <Stack direction="row" sx={{ width: "100%", mt: 1 }}>
                  <Stack direction={isScreenSmall ? "column" : "row"} sx={{ width: "100%", gap: 1 }}>
                    <Stack sx={{ width: isScreenSmall ? "100%" : "50%" }}>
                      <Typography sx={{ fontSize: "13px" }}>Category</Typography>
                      <Controller
                        control={control}
                        name="CategoryId"
                        render={({ field: { ref, value, onChange } }) => {
                          return (
                            <Autocomplete
                              multiple
                              ref={ref}
                              size="small"
                              value={value}
                              options={
                                editData
                                  ? categoryData?.value?.category?.filter(
                                      (item) => item.channelId === watch("ChannelId")?.channelId,
                                    ) || []
                                  : categoryData?.value?.category?.filter(
                                      (item) => item.channelId === watch("ChannelId")?.id,
                                    ) || []
                              }
                              loading={categoryIsLoading}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Select Category"
                                  sx={{ "& .MuiInputBase-input": { fontSize: "13px" } }}
                                />
                              )}
                              onOpen={() => {
                                if (!categoryIsSuccess)
                                  getCategory({
                                    Status: true,
                                  });
                              }}
                              onChange={(_, value) => {
                                onChange(value);

                                const categoryIdParams = value?.map((category) => category?.id);

                                if (watch("CategoryId").length === 0) {
                                  setValue("SubCategoryId", []);
                                }

                                getSubCategory({
                                  CategoryId: categoryIdParams,
                                });
                              }}
                              getOptionLabel={(option) => option.category_Description || ""}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              getOptionDisabled={(option) =>
                                watch("CategoryId").some((item) => item.id === option.id)
                              }
                              sx={{
                                flex: 2,
                              }}
                              fullWidth
                              disablePortal
                              disableClearable
                              disabled
                              componentsProps={{
                                popper: {
                                  sx: {
                                    "& .MuiAutocomplete-listbox": {
                                      fontSize: "13px",
                                    },
                                  },
                                },
                              }}
                            />
                          );
                        }}
                      />
                    </Stack>

                    <Stack sx={{ width: isScreenSmall ? "100%" : "50%" }}>
                      <Typography sx={{ fontSize: "13px" }}>Sub Category</Typography>
                      <Controller
                        control={control}
                        name="SubCategoryId"
                        render={({ field: { ref, value, onChange } }) => {
                          return (
                            <Autocomplete
                              multiple
                              ref={ref}
                              size="small"
                              value={value}
                              options={subCategoryData?.value || []}
                              loading={subCategoryIsLoading}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Select Sub Category"
                                  sx={{ "& .MuiInputBase-input": { fontSize: "13px" } }}
                                />
                              )}
                              // onOpen={() => {
                              //   if (!subCategoryIsSuccess) getSubCategory();
                              // }}
                              onChange={(_, value) => {
                                onChange(value);
                              }}
                              getOptionLabel={(option) => option?.sub_Category_Description || ""}
                              isOptionEqualToValue={(option, value) =>
                                option?.subCategoryId === value?.subCategoryId
                              }
                              getOptionDisabled={(option) =>
                                watch("SubCategoryId").some(
                                  (item) => item.subCategoryId === option.subCategoryId,
                                )
                              }
                              noOptionsText={"No sub category available"}
                              sx={{
                                flex: 2,
                              }}
                              fullWidth
                              disablePortal
                              disableClearable
                              disabled
                              componentsProps={{
                                popper: {
                                  sx: {
                                    "& .MuiAutocomplete-listbox": {
                                      fontSize: "13px",
                                    },
                                  },
                                },
                              }}
                            />
                          );
                        }}
                      />
                    </Stack>
                  </Stack>
                </Stack>

                <Stack direction="row" sx={{ width: "100%", mt: 1 }}>
                  <Stack direction={isScreenSmall ? "column" : "row"} sx={{ width: "100%", gap: 1 }}>
                    {/* Date Needed */}
                    <Stack sx={{ width: "100%" }}>
                      <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
                        <Typography sx={{ fontSize: "13px" }}>Date Needed</Typography>
                        <FiberManualRecord color="error" sx={{ fontSize: "10px" }} />
                      </Stack>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <Controller
                          control={control}
                          name="DateNeeded"
                          render={({ field }) => {
                            return (
                              <DatePicker
                                value={field.value ? moment(field.value) : moment()}
                                onChange={(newValue) => {
                                  const formattedValue = newValue
                                    ? moment(newValue).format("YYYY-MM-DD")
                                    : null;
                                  field.onChange(formattedValue);
                                }}
                                slotProps={{
                                  textField: {
                                    variant: "outlined",
                                    sx: {
                                      "& .MuiInputBase-input": {
                                        padding: "8.5px 14px",
                                      },
                                      "& .MuiOutlinedInput-root": {
                                        fontSize: "13px",
                                      },
                                    },
                                  },
                                }}
                                minDate={dateNeededValidation}
                                error={!!errors.DateNeeded}
                                helperText={errors.DateNeeded}
                                disabled={editData?.concern_Status !== "For Approval" || backjobButton}
                              />
                            );
                          }}
                        />
                        {errors.DateNeeded && (
                          <Typography sx={{ color: "theme.palette.error.main" }}>
                            {errors.DateNeeded.message}
                          </Typography>
                        )}
                      </LocalizationProvider>
                    </Stack>

                    <Stack sx={{ width: isScreenSmall ? "100%" : "50%", display: "none" }}>
                      {/* Target Date */}
                      <Typography sx={{ fontSize: "13px" }}>Target Date: </Typography>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <Controller
                          control={control}
                          name="TargetDate"
                          render={({ field }) => (
                            <DatePicker
                              value={field.value ? moment(field.value) : null}
                              onChange={(newValue) => {
                                const formattedValue = newValue
                                  ? moment(newValue).format("YYYY-MM-DD")
                                  : null;
                                field.onChange(formattedValue);
                              }}
                              slotProps={{
                                textField: {
                                  variant: "outlined",
                                  sx: {
                                    "& .MuiInputBase-input": {
                                      padding: "8.5px 14px",
                                    },
                                    "& .MuiOutlinedInput-root": {
                                      fontSize: "13px",
                                    },
                                  },
                                },
                              }}
                              minDate={new moment()}
                              error={!!errors.TargetDate}
                              helperText={errors.TargetDate ? errors.TargetDate.message : null}
                              disabled
                              // disabled={editData?.concern_Status === "" || editData?.concern_Status === "For Approval" ? false : true}
                            />
                          )}
                        />
                        {errors.TargetDate && <Typography>{errors.TargetDate.message}</Typography>}
                      </LocalizationProvider>
                    </Stack>
                  </Stack>
                </Stack>

                <Stack sx={{ width: "100%", mt: 1, display: "none" }}>
                  {/* Assign to */}
                  <Typography
                    sx={{
                      fontSize: "13px",
                    }}
                  >
                    Assign To:
                  </Typography>
                  <Controller
                    control={control}
                    name="IssueHandler"
                    render={({ field: { ref, value, onChange } }) => {
                      return (
                        <Autocomplete
                          ref={ref}
                          size="small"
                          value={value ?? null}
                          options={
                            issueHandlerData?.value?.channel?.find(
                              (item) => item.id === watch("ChannelId")?.channelId,
                            )?.channelUsers || []
                          }
                          loading={issueHandlerIsLoading}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Issue Handler"
                              sx={{ "& .MuiInputBase-input": { fontSize: "13px" } }}
                            />
                          )}
                          onOpen={() => {
                            if (!issueHandlerIsSuccess) getIssueHandler();
                          }}
                          onChange={(_, value) => {
                            onChange(value);
                          }}
                          getOptionLabel={(option) => option.fullname ?? null}
                          isOptionEqualToValue={(option, value) => option?.userId === value?.userId}
                          sx={{
                            flex: 2,
                          }}
                          componentsProps={{
                            popper: {
                              sx: {
                                "& .MuiAutocomplete-listbox": {
                                  fontSize: "13px",
                                },
                              },
                            },
                          }}
                          fullWidth
                          disabled
                          // disablePortal
                          // disabled={editData?.concern_Status === "" || editData?.concern_Status === "For Approval" ? false : true}
                        />
                      );
                    }}
                  />
                </Stack>

                {/* DESCRIPTION AND ATTACHMENT */}
                <Stack
                  width="100%"
                  sx={{
                    paddingTop: 2,
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
                    <Typography sx={{ fontSize: "13px" }}>Concern Details</Typography>
                    <FiberManualRecord color="error" sx={{ fontSize: "10px" }} />
                  </Stack>

                  <Controller
                    control={control}
                    name="Concern"
                    render={({ field: { ref, value, onChange } }) => {
                      return (
                        <TextField
                          inputRef={ref}
                          size="medium"
                          value={value}
                          placeholder="Enter Concern Details"
                          onChange={onChange}
                          error={!!errors.Concern}
                          sx={{
                            width: "100%",
                          }}
                          InputProps={{
                            style: {
                              fontSize: "13px",
                            },
                          }}
                          InputLabelProps={{
                            style: {
                              fontSize: "13px",
                            },
                          }}
                          disabled={
                            editData?.concern_Status === "Ongoing" ||
                            editData?.concern_Status !== "For Confirmation" ||
                            editData?.concern_Status === "Done" ||
                            backjobButton
                          }
                          autoComplete="off"
                          rows={6}
                          multiline
                        />
                      );
                    }}
                  />
                  {errors.Concern && (
                    <Typography color="error" sx={{ fontSize: "12px" }}>
                      {errors.Concern.message}
                    </Typography>
                  )}
                </Stack>

                <Stack
                  width="100%"
                  sx={{
                    paddingTop: 2,
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Typography sx={{ fontSize: "13px" }}>Notes (Optional):</Typography>

                  <Controller
                    control={control}
                    name="Notes"
                    render={({ field: { ref, value, onChange } }) => {
                      return (
                        <TextField
                          inputRef={ref}
                          size="medium"
                          value={value}
                          placeholder="Add notes"
                          onChange={onChange}
                          sx={{
                            width: "100%",
                          }}
                          InputProps={{
                            style: {
                              fontSize: "13px",
                            },
                          }}
                          InputLabelProps={{
                            style: {
                              fontSize: "13px",
                            },
                          }}
                          autoComplete="off"
                          rows={4}
                          multiline
                          disabled={
                            editData?.concern_Status === "" || editData?.concern_Status === "For Approval"
                              ? false
                              : true
                          }
                        />
                      );
                    }}
                  />
                </Stack>

                {/* ATTACHMENTS */}
                <Stack
                  width="100%"
                  sx={{
                    paddingTop: 2,
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Typography sx={{ fontSize: "13px" }}>Attachment:</Typography>

                  <Stack
                    sx={{
                      width: "100%",
                      display: "flex",
                      border: "2px dashed #2D3748",
                      justifyContent: "left",
                      padding: 1,
                    }}
                    onDragOver={handleDragOver}
                  >
                    {(editData?.concern_Status === "For Approval" || editData?.concern_Status === "") && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 1,
                          alignItems: "center",
                        }}
                      >
                        <Button
                          size="small"
                          variant="contained"
                          color="warning"
                          onClick={handleUploadButtonClick}
                        >
                          <Typography sx={{ fontSize: "12px" }}>Choose File</Typography>
                        </Button>

                        <Typography sx={{ color: theme.palette.text.secondary, fontSize: "13px" }}>
                          .docx, .jpg, .jpeg, .png, .pdf file
                        </Typography>
                      </Box>
                    )}

                    <Divider
                      variant="fullWidth"
                      sx={{
                        display: !attachments?.length ? "none" : "flex",
                        background: "#2D3748",
                        marginTop: 1,
                      }}
                    />

                    {attachments === undefined ? (
                      <Stack sx={{ flexDirection: "column", maxHeight: "auto", padding: 4 }}>
                        <Stack direction="row" gap={0.5} justifyContent="center">
                          <AttachFileOutlined sx={{ color: theme.palette.text.secondary }} />
                          <Typography sx={{ color: theme.palette.text.secondary, fontSize: "13px" }}>
                            No attached file
                          </Typography>
                        </Stack>
                      </Stack>
                    ) : (
                      <Stack
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          maxHeight: "auto",
                        }}
                      >
                        {attachments?.map((fileName, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              width: "100%",
                              flexDirection: "row",
                              alignItems: "center",
                              padding: 1,
                              maxWidth: "100%",
                              overflow: "hidden",
                            }}
                          >
                            {getAttachmentIcon(fileName)}
                            <Box
                              sx={{
                                width: "100%",
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: 0.5,
                                borderBottom: "1px solid #2D3748",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <Typography sx={{ fontWeight: 500, fontSize: "13px" }}>
                                  {fileName.name}
                                </Typography>

                                <Typography
                                  sx={{
                                    fontSize: "13px",
                                    fontWeight: 500,
                                    color: theme.palette.text.secondary,
                                  }}
                                >
                                  {fileName.size} Mb
                                </Typography>

                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "13px",
                                      fontWeight: 500,
                                      color: !!fileName.ticketAttachmentId
                                        ? theme.palette.success.main
                                        : theme.palette.primary.main,
                                    }}
                                  >
                                    {!!fileName.ticketAttachmentId
                                      ? "Attached file"
                                      : "Uploaded the file successfully"}
                                  </Typography>

                                  {!!fileName.ticketAttachmentId && (
                                    <CheckOutlined color="success" fontSize="small" />
                                  )}
                                </Box>
                              </Box>

                              <Box>
                                <ConcernViewMenuActions
                                  editData={editData}
                                  fileName={fileName}
                                  onView={handleViewImage}
                                  onViewWithoutId={handleViewImageWithoutId}
                                  onDelete={handleDeleteFile}
                                  onDownload={handleDownloadAttachment}
                                  isImageFile={isImageFile}
                                  viewIsLoading={viewIsLoading}
                                  downloadIsLoading={downloadIsLoading}
                                  deleteIsLoading={deleteIsLoading}
                                />
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Stack>

                  <Controller
                    control={control}
                    name="RequestAttachmentsFiles"
                    render={({ field: { onChange, value } }) => (
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".png,.jpg,.jpeg,.docx,.pdf,.xls,.xlsx"
                        onChange={(event) => {
                          if (ticketAttachmentId) {
                            const files = Array.from(event.target.files);
                            files[0].ticketAttachmentId = ticketAttachmentId;
                            const uniqueNewFiles = files.filter(
                              (item) => !value?.some((file) => file.name === item.name),
                            );

                            onChange([
                              ...files,
                              ...value.filter((item) => item.ticketAttachmentId !== ticketAttachmentId),
                              ...!uniqueNewFiles,
                            ]);

                            setAttachments((prevFiles) => [
                              ...prevFiles.filter((item) => item.ticketAttachmentId !== ticketAttachmentId),
                              {
                                ticketAttachmentId: ticketAttachmentId,
                                name: files[0].name,
                                size: (files[0].size / (1024 * 1024)).toFixed(2),
                              },
                            ]);

                            fileInputRef.current.value = "";
                            setTicketAttachmentId(null);
                          } else {
                            handleAttachments(event);
                            const files = Array.from(event.target.files);
                            const uniqueNewFiles = files.filter(
                              (item) => !attachments?.some((file) => file.name === item.name),
                            );

                            onChange([...value, ...uniqueNewFiles]);
                            fileInputRef.current.value = "";
                          }
                        }}
                        hidden
                        multiple={!!ticketAttachmentId}
                      />
                    )}
                  />
                </Stack>
              </Stack>
            </Stack>
          </DialogContent>

          <DialogActions sx={{ position: "sticky", bottom: 0, backgroundColor: theme.palette.bgForm.black3 }}>
            <Button
              variant="outlined"
              disabled={
                isCreateEditRequestorConcernLoading || isCreateEditRequestorConcernFetching || isLoading
              }
              onClick={onCloseAction}
              sx={{
                ":disabled": {
                  backgroundColor: "none",
                  color: "black",
                },
              }}
            >
              Close
            </Button>

            {editData?.concern_Status === "" || editData?.concern_Status === "For Approval" ? (
              <LoadingButton
                size="large"
                variant="contained"
                color="primary"
                type="submit"
                loading={
                  isCreateEditRequestorConcernLoading || isCreateEditRequestorConcernFetching || isLoading
                }
                disabled={
                  !watch("ServiceProvider") ||
                  !watch("DateNeeded") ||
                  !watch("Concern") ||
                  moment(watch("DateNeeded")).format("MM-DD-YYYY") <
                    moment(dateNeededValidation).format("MM-DD-YYYY") ||
                  (watch("Request_Type") === "Rework" && !watch("BackJobId")) ||
                  (editData?.backJobId && !watch("Notes"))
                }
              >
                Save
              </LoadingButton>
            ) : (
              ""
            )}
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog to view image */}
      {selectedImage && (
        <>
          <Dialog fullWidth maxWidth="md" open={isViewDialogOpen} onClose={handleViewClose}>
            <DialogContent sx={{ height: "auto" }}>
              {selectedImage && <img src={selectedImage} alt="Preview" style={{ width: "100%" }} />}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleViewClose}>Close</Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
};

export default ConcernViewDialog;
